/**
 * Hand-written, IRREDUCIBLE hooks for `@inferrex/api-client`.
 *
 * Everything the codegen CAN derive lives in `hooks.generated.ts`. This file holds only the
 * cases the generator structurally cannot emit:
 *   - polling reads (`refetchInterval`): intelligence dashboard, usage meters, connection status
 *   - golden-layer resolvers with bespoke positional-arg signatures + `enabled: count>=2`
 *   - the `apiFetch`-backed hooks for routes that have NO SDK method (MFA, API keys, billing,
 *     workspace/team/SSO/env CRUD, onboarding, mappings, oauth-initiate)
 *   - the response type the frontend imports by name (`OnboardingSession`)
 *
 * The bespoke parts of these hooks NAME the reusable primitives in `./logic` (enabled-when-count)
 * rather than re-inlining cache logic (Rule 33/36). Their backing routes carry
 * `clientHook: { skip: true }` so the generator does not also emit them.
 */

import { useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClient, currentApiToken, currentApiBaseUrl, applyContextHeaders,
  currentActiveEnvironmentId, setActiveEnvironmentId,
  currentActiveWorkspaceId, setActiveWorkspaceId, queryClient,
  resolveApiToken, clearBrowserReauthGuard, contextFetch, setTenantEntryToken,
  newIdempotencyKey,
} from './client-runtime.js';
import { enabledWhenCount } from './logic/index.js';
import { IDEMPOTENCY_HEADER } from '@inferrex/sdk';
import type {
  InferrexApiError,
  MappingManifest,
  OAuthInitiateRequest,
} from '@inferrex/contracts';

// ── Authenticated fetch (for routes with no SDK method) ────────────────────────

/** Authenticated fetch against the API base URL — the bearer token + base URL live in client-runtime. */
async function apiFetch<T>(path: string, options?: RequestInit & { readonly idempotent?: boolean }): Promise<T> {
  const baseUrl = currentApiBaseUrl();
  const headers = new Headers(options?.headers);
  const token = (await resolveApiToken()) ?? currentApiToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // The same ambient workspace + environment the SDK's bearerFetch sends — these two paths
  // must agree, or a page built from both would mix contexts within one render.
  applyContextHeaders(headers);
  if (!headers.has('Content-Type') && options?.method && options.method !== 'GET' && options.method !== 'DELETE') {
    headers.set('Content-Type', 'application/json');
  }
  // Every authenticated write accepts an Idempotency-Key (scripts/generate/lib/idempotency.ts);
  // the generated hooks send one through the SDK, and these hand-written ones must not differ.
  const method = (options?.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && options?.idempotent !== false && !headers.has(IDEMPOTENCY_HEADER)) {
    headers.set(IDEMPOTENCY_HEADER, newIdempotencyKey());
  }
  const res = await contextFetch(`${baseUrl}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText })) as InferrexApiError | { message?: string; error?: undefined };
    const err = new Error(body.message ?? `HTTP ${res.status}`) as Error & { status: number; code?: string; body?: InferrexApiError };
    err.status = res.status;
    // The API's stable error code (e.g. tenant_entry_required), for callers that branch on it.
    if (typeof body.error === 'string') {
      err.code = body.error;
      // The whole envelope (retryable, docs_url, requestId, details), so the UI can decide
      // whether to offer a retry and can show the reference — the same as an SDK error carries.
      err.body = body;
    }
    throw err;
  }
  clearBrowserReauthGuard();
  return res.json() as Promise<T>;
}

// ── Live event stream (POST /v1/events/stream-ticket → GET /v1/events/stream) ─────────────
// Both routes are `special: 'sse'`, so the SDK has no method for them: the stream is a long-lived
// EventSource, not a request/response. The paths live HERE, beside every other hand-written route,
// so an app opens the stream without a `/v1/` literal of its own (frontend-api-contract-validator).

/** A single-use ticket, valid for about a minute, that opens the event stream once. */
export interface EventStreamTicket {
  readonly ticket: string;
  readonly expiresInSeconds: number;
}

/**
 * Mint a stream ticket over a normal authenticated request, so the access token never rides in
 * the EventSource URL (query strings land in proxy logs and browser history). Carries the same
 * workspace and environment headers as every other call. No Idempotency-Key: a replayed answer
 * would hand back a ticket that has already been spent, and the cached copy would sit in the
 * idempotency table for a day.
 */
export function mintEventStreamTicket(): Promise<EventStreamTicket> {
  return apiFetch<EventStreamTicket>('/v1/events/stream-ticket', { method: 'POST', idempotent: false });
}

/** The EventSource address for a minted ticket. */
export function eventStreamUrl(ticket: string): string {
  return `${currentApiBaseUrl()}/v1/events/stream?ticket=${encodeURIComponent(ticket)}`;
}

// ── Golden-layer resolvers (deterministic, 0 AI ops) — bespoke positional-arg signatures ──
// Suggestions, capabilities AND routing are precisely typed by the SDK: the route responseBody
// declares the nested element shape (routing's `bySystem` via `recordValueType: 'string[]'` →
// Record<string, string[]>), so the hooks just unwrap with no cast.

/** "What to connect next" — deterministic canonical-concept overlap. */
export function useConnectionSuggestions(vendorKey: string, connected?: string) {
  return useQuery({
    queryKey: ['golden-layer', 'suggestions', vendorKey, connected ?? ''],
    queryFn: async () => {
      const res = await getClient().connections.connectionSuggestions(
        vendorKey, connected ? { connected } : undefined);
      return res.suggestions ?? [];
    },
    enabled: !!vendorKey,
  });
}

/** A vendor's ranked multi-vertical capabilities. */
export function useConnectionCapabilities(vendorKey: string) {
  return useQuery({
    queryKey: ['golden-layer', 'capabilities', vendorKey],
    queryFn: async () => {
      const res = await getClient().connections.connectionCapabilities(vendorKey);
      return res.capabilities ?? [];
    },
    enabled: !!vendorKey,
  });
}

/** Facet-safe field routing across connected systems (?systems=a,b,c). */
export function useConnectionRouting(systems: string) {
  return useQuery({
    queryKey: ['golden-layer', 'routing', systems],
    queryFn: async () => {
      const res = await getClient().connections.connectionRouting({ systems });
      return res.routes ?? [];
    },
    enabled: enabledWhenCount(2)(systems),
  });
}

// ── Connection status (polling while the manifest is being inferred) ────────────

export type ConnectionCadencePreset = 'realtime' | '5m' | '15m' | 'hourly' | 'daily' | 'weekly' | 'manual';

export interface ConnectionCadence {
  connectionId: string;
  preset: ConnectionCadencePreset | null;
  scheduleType: string | null;
  scheduleCron: string | null;
  /** Per-entity cadences stored on the connection (GET only), keyed as written — an entity
   *  id from the onboarding wizard, an api name from the mapping-setup screen. */
  entities?: Record<string, ConnectionCadencePreset>;
  /** Per entity api name (GET only): its last successful sync, the effective cadence it ran
   *  under, and when it is next due — null when manual or when no cadence is set (every run). */
  entitySync?: Record<string, { lastSyncedAt: string; cadence: ConnectionCadencePreset | null; nextDueAt: string | null }>;
  pipelines?: number;
  ingestPath?: string | null;
  ingestToken?: string | null;
}

export function useConnectionCadence(connectionId: string) {
  return useQuery({
    queryKey: ['connections', connectionId, 'cadence'],
    queryFn: () => apiFetch<ConnectionCadence>(`/v1/connections/${connectionId}/cadence`),
    enabled: !!connectionId,
  });
}

export function useSetConnectionCadence(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { preset: ConnectionCadencePreset; entities?: Record<string, ConnectionCadencePreset> }) =>
      apiFetch<ConnectionCadence>(`/v1/connections/${connectionId}/cadence`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      // The POST answers with the governing cadence only; the GET also carries the
      // per-entity map, so keep what was cached and refetch the rest.
      qc.setQueryData<ConnectionCadence>(['connections', connectionId, 'cadence'], (prev) => ({ ...prev, ...data }));
      void qc.invalidateQueries({ queryKey: ['connections', connectionId, 'cadence'] });
    },
  });
}

export function useConnectionStatus(connectionId: string) {
  return useQuery({
    queryKey: ['connections', connectionId, 'status'],
    queryFn: () => getClient().connections.getConnectionStatus(connectionId),
    enabled: !!connectionId,
    // Poll while the manifest is still being inferred. Once it's approved/rejected,
    // back off — the status won't change again without user action.
    refetchInterval: (q) => {
      const data = q.state.data as { manifestStatus?: string | null } | undefined;
      if (data?.manifestStatus === 'approved' || data?.manifestStatus === 'rejected') return false;
      return 3000;
    },
    refetchIntervalInBackground: false,
  });
}

// Suggestion accept/dismiss is no longer a hook: the shared @inferrex/ui
// `suggestion-list` block (dashboard + pipeline-detail tab) fires accept/dismiss
// through the DataBinding's runAction (which reloads the bound list), so the
// bespoke optimistic-remove hooks were deleted (Rule 35). The SDK
// pipelines.accept/dismissSuggestion methods remain available for direct callers.

// ── Intelligence + usage (live dashboards — refetchInterval) ────────────────────
//
// The intelligence dashboard page (/intelligence) is now schema-driven
// (page-manifests/intelligence-dashboard.ts) and binds each sub-array per-entity
// via APP_BINDINGS — so the composite useIntelligenceDashboard hook was removed
// (Rule 35). The composite GET /v1/intelligence/dashboard route + its SDK method
// remain as a public API surface; re-add a hook here if a consumer needs it.

/** Seed of `monitoring.pollSeconds`. Replaced from the resolved workspace config. */
const POLL_SECONDS_MIN = 5;
const POLL_SECONDS_MAX = 600;
let livePollMs = 30_000;

export function applyLivePollSeconds(value: unknown): void {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return;
  const seconds = Math.min(POLL_SECONDS_MAX, Math.max(POLL_SECONDS_MIN, n));
  livePollMs = seconds * 1000;
}

export function livePollIntervalMs(): number {
  return livePollMs;
}

export function useUsageMeters() {
  return useQuery({
    queryKey: ['usage'],
    queryFn: () => getClient().intelligence.getUsageMeters(),
    // Function form so a later applyLivePollSeconds is picked up on the next tick.
    refetchInterval: () => livePollMs,
  });
}

// ── Mappings (apiFetch — read + customer override write-through) ─────────────────

export function useMapping(mappingId: string) {
  return useQuery({
    queryKey: ['mappings', mappingId],
    queryFn: () => apiFetch<MappingManifest>(`/v1/mappings/${mappingId}`),
    enabled: !!mappingId,
  });
}

// ── Re-approval flow (MASTER-EXECUTION-PLAN A1, audit §2.2) ──
// A pipeline pins its mapping version at activation; edits create new versions the
// pipeline never sees until explicitly re-applied. The banner query + the apply action.

export interface MappingPipelineRow {
  id: string;
  name: string;
  status: string;
  pinnedMappingId: string;
  headMappingId: string;
  upToDate: boolean;
}

export interface MappingPipelines {
  pipelines: MappingPipelineRow[];
  pipelineCount: number;
  headMappingId: string;
}

export function useMappingPipelines(mappingId: string) {
  return useQuery({
    queryKey: ['mappings', mappingId, 'pipelines'],
    queryFn: () => apiFetch<MappingPipelines>(`/v1/mappings/${mappingId}/pipelines`),
    enabled: !!mappingId,
  });
}

// Advance a pipeline's pinned mapping to the reviewed head. mappingVersionId MUST be the
// head the customer just looked at — the server 409s if the lineage moved since (§12:
// the approval binds to a version a human actually saw).
export function useApplyMappingToPipeline(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, mappingVersionId }: { pipelineId: string; mappingVersionId: string }) =>
      apiFetch<{ pipelineId: string; appliedMappingId: string; upToDate: boolean }>(
        `/v1/pipelines/${pipelineId}/apply-mapping`,
        { method: 'POST', body: JSON.stringify({ mappingVersionId }) },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] }); },
  });
}

// One live record for the preview's fetch button (A7, §2.7). A plain function, not a
// hook — the fetch is user-triggered and its result feeds component-local state.
export interface MappingSampleResult {
  record: Record<string, unknown> | null;
  entityType: string;
  side: string;
}

export function fetchMappingSample(mappingId: string, side: 'source' | 'target', entity: string): Promise<MappingSampleResult> {
  const params = new URLSearchParams({ side, ...(entity !== '' ? { entity } : {}) });
  return apiFetch<MappingSampleResult>(`/v1/mappings/${mappingId}/sample?${params.toString()}`);
}

// One live record through the mapping (the Record view). A plain function, like the
// sample fetch — the page owns offset and falls back to fetchMappingSample when this
// route is not on the API image yet (404 Endpoint not found / No such route).
export interface MappingEstateField {
  sourceField: string;
  targetField: string;
  sourceLabel: string;
  targetLabel: string;
  sourceValue: string | null;
  targetValue: string | null;
  verdict?: {
    agreement: 'match' | 'equivalent' | 'different' | 'one-sided';
    agreementLabel: string;
    goldenValue: string | null;
    winner: 'source' | 'target' | 'either' | 'unresolved';
    goldenLabel: string;
  };
}

export interface MappingEstateResult {
  sourceSystem: string;
  targetSystem: string;
  sourceEntity: string;
  targetEntity: string;
  sourceEntityLabel: string;
  targetEntityLabel: string;
  offset: number;
  total: number;
  matched: boolean;
  label: string;
  note: string | null;
  join: {
    sourceField: string;
    targetField: string;
    sourceLabel: string;
    targetLabel: string;
    sourceValue: string;
    targetValue: string | null;
  } | null;
  written: MappingEstateField[];
  held: Array<MappingEstateField & { reason: string }>;
  otherPairs: number;
  relation?: {
    kind: 'none' | 'one-to-one' | 'one-to-many' | 'many-to-one' | 'many-to-many';
    sourceCount: number;
    targetCount: number;
    label: string;
  };
  /** Other connected systems that share this email. Absent on the sample fallback. */
  places?: Array<{ system: string; entity: string; count: number; note: string }> | null;
  /** Row count and unique record count for each system. Copies of one email or phone count once. */
  census?: Array<{ system: string; entity: string; rows: number | null; unique: number | null; note: string | null }> | null;
  /** Set only by the page when it built this from the sample endpoint. The API never sends it. */
  fallback?: boolean;
}

export function fetchMappingEstate(
  mappingId: string,
  query: { offset?: number; entity?: string } = {},
): Promise<MappingEstateResult> {
  const params = new URLSearchParams();
  if (query.offset !== undefined && query.offset > 0) params.set('offset', String(query.offset));
  if (query.entity) params.set('entity', query.entity);
  const qs = params.toString();
  return apiFetch<MappingEstateResult>(`/v1/mappings/${mappingId}/estate${qs ? `?${qs}` : ''}`);
}

// Intercom messenger boot config (C7). enabled:false until the server has the identity
// secret — the messenger must never boot without server-signed identity verification.
export interface MessengerConfig {
  enabled: boolean;
  appId?: string;
  email?: string;
  userHash?: string;
}

export function fetchMessengerConfig(): Promise<MessengerConfig> {
  return apiFetch<MessengerConfig>('/v1/support/messenger');
}

export interface FieldMappingPatch {
  fieldMappingId: string;
  transform?: string;
  /** Per-transform configuration (A5) — null clears it. */
  transformConfig?: Record<string, unknown> | null;
  authority?: string;
  /** Per-field sync cadence override (§0.1 per-field sync tiers). */
  syncTier?: string;
  /** Per-link predicate (§7) — null clears it. */
  condition?: { field: string; op: string; value?: string | undefined } | null;
  /** Per-field flow (item 11): source-to-target | target-to-source | bidirectional. */
  direction?: string;
  approved?: boolean;
  /** Omit/restore — a business decision, separate from reject; never touches approval. */
  omitted?: boolean;
}

// Customer override of one field mapping (transform / authority / approve-reject). Writes
// back the whole MappingManifest and seeds the cache so the review UI reflects it at once.
export function useUpdateFieldMapping(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: FieldMappingPatch): Promise<MappingManifest> =>
      apiFetch<MappingManifest>(`/v1/mappings/${mappingId}/field-mapping`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['mappings', mappingId], data);
      // An edit creates a NEW manifest version, so any pipeline pinned at the old one is
      // now stale — refresh the re-approval banner (A1).
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] });
    },
  });
}

export interface BulkFieldMappingPatch {
  fieldMappingIds: string[];
  /** approve | reject | omit | restore | amend — open string, server validates. */
  action: string;
  transform?: string;
  authority?: string;
  syncTier?: string;
  direction?: string;
}

// One bulk review action over a selection — persisted server-side as a SINGLE manifest
// version, so the whole bulk action is one undoable entry in history (§11a).
export function useBulkUpdateFieldMappings(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: BulkFieldMappingPatch): Promise<MappingManifest> =>
      apiFetch<MappingManifest>(`/v1/mappings/${mappingId}/field-mappings`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['mappings', mappingId], data);
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] });
    },
  });
}

// ── Field groups (§1.4, task 8) ────────────────────────────────────────────────

export interface FieldGroupRecommendation {
  entityId: string;
  entityName: string;
  fieldId: string;
  group: string;
  basis: 'role' | 'type';
  /** Plain-language rationale — deterministic grouping earns a sentence, not a score. */
  why: string;
  archetypeCorroborated: boolean;
}

export interface FieldGroupRecommendations {
  connectionId: string;
  manifestId: string;
  recommendations: FieldGroupRecommendation[];
  recommendationCount: number;
  /** Groups already in use — a picker offers these first. */
  groups: string[];
  /** The full recommendable set, so a picker can also offer what has not been used yet. */
  catalogue: { key: string; label: string; description: string }[];
}

/** Deterministic group recommendations from role + L2 type + L4 archetype. No new AI. */
export function useFieldGroupRecommendations(connectionId: string) {
  return useQuery({
    queryKey: ['connections', connectionId, 'field-group-recommendations'],
    queryFn: () => apiFetch<FieldGroupRecommendations>(`/v1/connections/${connectionId}/field-group-recommendations`),
    enabled: !!connectionId,
  });
}

/** Replace the group-scoped destination sets on a mapping. `[]` removes group fan-out. */
export function useSetGroupDestinations(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupDestinations: unknown[] }): Promise<MappingManifest> =>
      apiFetch<MappingManifest>(`/v1/mappings/${mappingId}/group-destinations`, {
        method: 'PUT',
        body: JSON.stringify(vars),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['mappings', mappingId], data);
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] });
    },
  });
}

// ── Fan-out destinations (§2.4/§3.4) ───────────────────────────────────────────

/**
 * Replace the destination SET for one field mapping. A PUT, not a patch: the set is edited
 * as a whole so removing a destination is expressible, and `[]` clears them all.
 */
export function useSetFieldMappingDestinations(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fieldMappingId: string; destinations: unknown[] }): Promise<MappingManifest> =>
      apiFetch<MappingManifest>(`/v1/mappings/${mappingId}/field-mapping/destinations`, {
        method: 'PUT',
        body: JSON.stringify(vars),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['mappings', mappingId], data);
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] });
    },
  });
}

// ── Manual mapping (§2.4) — the picker vocabulary + the create path ────────────

/** One selectable field in a mapping picker — everything the human needs to tell two
 *  similarly-named fields apart, drawn from the inferred manifest. */
export interface MappableField {
  id: string;
  apiName: string;
  displayName: string;
  type: string;
  required: boolean;
  role?: string;
  description?: string;
  /** The field's group (§1.4), when it has one — the open, tenant-defined label. */
  group?: string;
  /** Withheld (empty) for sensitive fields, exactly as the schema review withholds them. */
  exampleValues: unknown[];
  /** A live field mapping already touches this field. */
  mapped: boolean;
}

export interface MappableEntity {
  id: string;
  apiName: string;
  displayName: string;
  archetype?: string;
  fields: MappableField[];
}

export interface MappableSide {
  connectionId: string;
  displayName: string;
  entities: MappableEntity[];
}

export interface MappableFields {
  source: MappableSide;
  target: MappableSide;
  unmappedSourceCount: number;
  unmappedTargetCount: number;
}

/**
 * Both connections' inferred fields, each flagged whether anything live maps it — the
 * unmapped queue and the manual-mapping pickers are the same question asked twice, so
 * they read the same endpoint rather than two that could disagree.
 */
export function useMappableFields(mappingId: string) {
  return useQuery({
    queryKey: ['mappings', mappingId, 'fields'],
    queryFn: () => apiFetch<MappableFields>(`/v1/mappings/${mappingId}/fields`),
    enabled: !!mappingId,
  });
}

export interface CreateFieldMappingRequest {
  sourceEntityId: string;
  sourceFieldId: string;
  targetEntityId: string;
  targetFieldId: string;
  transform?: string;
  transformConfig?: Record<string, unknown>;
  authority?: string;
  conflictStrategy?: string;
  syncTier?: string;
  direction?: string;
  condition?: { field: string; op: string; value?: string | undefined };
}

/**
 * Author a link the AI never proposed. The server validates both ends against the stored
 * connection manifests, so the picker options and what the API will accept cannot drift.
 */
export function useCreateFieldMapping(mappingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateFieldMappingRequest): Promise<MappingManifest> =>
      apiFetch<MappingManifest>(`/v1/mappings/${mappingId}/field-mapping`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['mappings', mappingId], data);
      // The new link changes what is unmapped, and creates a new manifest version that
      // any pinned pipeline is now behind (A1).
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'fields'] });
      void qc.invalidateQueries({ queryKey: ['mappings', mappingId, 'pipelines'] });
    },
  });
}

// ── Onboarding (apiFetch — always-200 { active } envelope, multi-step state machine) ──

export interface OnboardingSession {
  id: string;
  customerId: string;
  step: string;
  phase1ConnectionId: string | null;
  phase2ConnectionId: string | null;
  archivalEnabled: boolean | null;
  syncTierConfig: Record<string, unknown> | null;
  pipelineId: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * GET /v1/onboarding response envelope. `active: false` (with null id/step/createdAt)
 * means there is no in-progress session (mirrors GET /v1/terms/status) — a 200, not a 404.
 */
interface OnboardingEnvelope extends Partial<OnboardingSession> {
  active: boolean;
  customerId: string;
}

export function useOnboarding() {
  return useQuery({
    queryKey: ['onboarding'],
    // Returns null when onboarding is idle (active:false), an OnboardingSession otherwise —
    // so every consumer treats "no active onboarding" uniformly as `data == null`.
    queryFn: async (): Promise<OnboardingSession | null> => {
      const env = await apiFetch<OnboardingEnvelope>('/v1/onboarding');
      if (!env.active || env.id == null) return null;
      return env as OnboardingSession;
    },
  });
}

export function useStartOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<OnboardingSession> =>
      apiFetch<OnboardingSession>('/v1/onboarding/start', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

export function useAdvanceOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>): Promise<OnboardingSession> =>
      apiFetch<OnboardingSession>('/v1/onboarding/step', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** One entity's deterministic sync-tier suggestion (GET /v1/onboarding/entity-sync-tiers). */
export interface EntitySyncTierSuggestion {
  entityId: string;
  displayName: string;
  suggestedTier: string;
  rationale: string;
  confidence: number | null;
}

export interface EntitySyncTiersResponse {
  connectionId: string;
  suggestions: EntitySyncTierSuggestion[];
}

/**
 * Per-entity sync-tier suggestions for the phase-1 connection. Deterministic (0 AI ops) —
 * archetype → cadence. Keyed on connectionId so distinct connections cache independently;
 * disabled until a connectionId is known (empty query returns nothing to fetch).
 */
export function useOnboardingEntitySyncTiers(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ['onboarding', 'entity-sync-tiers', connectionId],
    enabled: Boolean(connectionId),
    queryFn: (): Promise<EntitySyncTiersResponse> =>
      apiFetch<EntitySyncTiersResponse>(`/v1/onboarding/entity-sync-tiers?connectionId=${connectionId}`),
  });
}

/** Archival-eligibility analysis (GET /v1/onboarding/archival-analysis). */
export interface OnboardingArchivalAnalysis {
  connectionId: string;
  /** Real count of archival-eligible entities. */
  eligibleEntities: number;
  /** Estimate — no data is synced yet during onboarding. Null when nothing is eligible. */
  estimatedRecords: number | null;
  /** Estimate — see estimatedRecords. Null when nothing is eligible. */
  estimatedMonthlySaving: number | null;
  /** Always true during onboarding: records/saving are estimates, not measured. */
  isEstimate: boolean;
}

/**
 * Archival-eligibility analysis for the phase-1 connection. eligibleEntities is real; the
 * record/saving figures are honest estimates (isEstimate). Keyed on connectionId; disabled
 * until a connectionId is known.
 */
export function useOnboardingArchivalAnalysis(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ['onboarding', 'archival-analysis', connectionId],
    enabled: Boolean(connectionId),
    queryFn: (): Promise<OnboardingArchivalAnalysis> =>
      apiFetch<OnboardingArchivalAnalysis>(`/v1/onboarding/archival-analysis?connectionId=${connectionId}`),
  });
}

// ── OAuth initiate (apiFetch) ───────────────────────────────────────────────────

export function useInitiateOAuth() {
  return useMutation({
    mutationFn: (req: OAuthInitiateRequest) =>
      apiFetch<{ authorizeUrl: string; state: string }>('/v1/oauth/initiate', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
  });
}

// ── Settings: API Keys + Billing (apiFetch) ─────────────────────────────────────

export interface ApiKeyItem {
  id: string;
  prefix: string;
  displayName: string | null;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface GeneratedApiKey extends ApiKeyItem {
  key: string; // Raw key — only returned on create
}

export function useApiKeys() {
  return useQuery({
    queryKey: ['settings', 'api-keys'],
    queryFn: async (): Promise<ApiKeyItem[]> => {
      const res = await apiFetch<{ keys: ApiKeyItem[] }>('/v1/settings/api-keys');
      return res.keys;
    },
  });
}

export function useGenerateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { displayName?: string; scopes?: string[]; expiresInDays?: number }) =>
      apiFetch<GeneratedApiKey>('/v1/settings/api-keys', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
    },
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiFetch<{ revoked: boolean }>(`/v1/settings/api-keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
    },
  });
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: (returnUrl?: string) =>
      apiFetch<{ portalUrl: string }>('/v1/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ returnUrl }),
      }),
  });
}

// ── Workspace Users (Team) (apiFetch — camelCase mapping over the snake_case CRUD list) ──

export interface WorkspaceUser {
  id: string;
  authSubjectId: string;
  email: string;
  displayName: string | null;
  role: 'viewer' | 'operator' | 'editor' | 'admin' | 'owner' | 'custom';
  isActive: boolean;
  acceptedAt: string | null;
  /** Lifecycle status column — e.g. 'pending_approval' | 'invited' | 'active' | 'pending'. */
  status: string | null;
  createdAt: string;
}

export interface WorkspaceUsersResponse {
  users: WorkspaceUser[];
  total: number;
  page: number;
  pageSize: number;
}

export function useWorkspaceUsers(page = 1, pageSize = 50) {
  return useQuery({
    queryKey: ['workspace', 'users', page, pageSize],
    queryFn: async (): Promise<WorkspaceUsersResponse> => {
      // The generated CRUD list returns `{ workspace_users: <snake_case rows> }` with
      // limit/offset paging (F9). Map to the camelCase WorkspaceUser shape the UI uses,
      // and carry the `status` column so pending_approval is surfaced (not derived).
      const offset = (page - 1) * pageSize;
      const raw = await apiFetch<{ workspace_users: Array<Record<string, unknown>> }>(
        `/v1/workspace/users?limit=${pageSize}&offset=${offset}`,
      );
      const rows = raw.workspace_users ?? [];
      const users: WorkspaceUser[] = rows.map((r) => ({
        id: String(r['id']),
        authSubjectId: String(r['auth_subject_id'] ?? ''),
        email: String(r['email'] ?? ''),
        displayName: (r['display_name'] as string | null) ?? null,
        role: r['role'] as WorkspaceUser['role'],
        isActive: Boolean(r['is_active']),
        acceptedAt: (r['accepted_at'] as string | null) ?? null,
        status: (r['status'] as string | null) ?? null,
        createdAt: String(r['created_at'] ?? ''),
      }));
      // The list endpoint returns no total; member lists are small, so page-1-of-all.
      return { users, total: users.length, page, pageSize };
    },
  });
}

export interface ApproveInviteResult {
  id: string;
  email: string | null;
  status: string;
  delivery: string | null;
  tempPassword: string | null;
  /** Present when delivery is 'email'. 'failed' = the invitee was not emailed. */
  emailStatus?: 'sent' | 'failed';
}

export function useApproveInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<ApproveInviteResult>(`/v1/workspace/users/${userId}/approve-invite`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'users'] }); },
  });
}

export function useRejectInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ id: string; status: string }>(`/v1/workspace/users/${userId}/reject-invite`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'users'] }); },
  });
}

export interface InviteResponse {
  id: string;
  email: string;
  role: string;
  status: 'active' | 'pending';
  /** Sovereign / enterprise only: one-time password the admin must deliver
   *  out-of-band. Absent in SaaS mode (where the user accepts via email). */
  tempPassword?: string;
  invitedAt?: string;
  /** SaaS only. 'failed' = the member was created but the invite email was not sent. */
  emailStatus?: 'sent' | 'failed';
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { email: string; role?: string }) =>
      apiFetch<InviteResponse>('/v1/workspace/invite', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'users'] });
    },
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    // Codegen emits PUT /v1/workspace/users/:id for full-record updates;
    // we only send the role field and the generated handler builds a dynamic
    // SET clause from whatever's in the body.
    mutationFn: (req: { userId: string; role: string }) =>
      apiFetch<{ success: boolean }>(`/v1/workspace/users/${req.userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: req.role }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'users'] });
    },
  });
}

export function useRemoveWorkspaceUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ success: boolean }>(`/v1/workspace/users/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'users'] });
    },
  });
}

// ── MFA (apiFetch) ──────────────────────────────────────────────────────────────

export interface MfaFactor {
  uid: string;
  displayName: string;
  factorId: string;
  enrollmentTime: string;
}

export interface MfaStatus {
  enrolled: boolean;
  factors: MfaFactor[];
  workspaceEnforces: boolean;
  enforcedAt: string | null;
  locked: boolean;
  lockoutMinutesRemaining: number;
}

export function useMfaStatus() {
  return useQuery({
    queryKey: ['settings', 'mfa', 'status'],
    queryFn: (): Promise<MfaStatus> =>
      apiFetch<MfaStatus>('/v1/settings/mfa/status'),
  });
}

// Workspace MFA enforcement (GET/PUT /v1/settings/workspace/mfa) is now a derived
// `workspace_mfa` singleton SettingsBlock on the Settings → Workspace tab — read
// + written through the AppPage binding runtime, so no hand-written hook here.

export function useMfaEnroll() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ secretKey: string; qrCodeUrl: string; sessionInfo: string }>('/v1/settings/mfa/enroll', {
        method: 'POST',
      }),
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    // The 6-digit TOTP code from the user's authenticator — proves they enrolled the pending secret
    // before it is promoted to active.
    mutationFn: (code: string) =>
      apiFetch<{ enrolled: boolean; factorCount: number }>('/v1/settings/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'mfa', 'status'] });
    },
  });
}

export function useMfaRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ removed: boolean }>('/v1/settings/mfa/remove', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'mfa', 'status'] });
    },
  });
}

export function useRecordMfaFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ recorded: boolean; locked: boolean; lockoutMinutesRemaining: number }>('/v1/settings/mfa/record-failure', {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'mfa', 'status'] });
    },
  });
}

// Platform config (the admin-tunable operational/display knobs) is now consumed
// entirely through the AppPage binding runtime: the derived Settings → Preferences
// tab's shared `tunables` component-block reads the composite { tunables, values }
// from the `platform_config` singleton binding and writes per-knob overrides via
// runAction, and health-score thresholds come from the binding's getScoreThresholds
// (rest-binding.ts) — so there are no hand-written platform-config hooks here.

// ── Team CRUD (apiFetch — flat composite-PK CRUD + client-side parent filter) ───

export interface Team {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
}

export interface TeamMember {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  addedAt: string;
}

export interface TeamResource {
  teamId: string;
  resourceType: 'connection' | 'pipeline' | 'environment' | 'workspace';
  resourceId: string;
  resourceName: string;
  taggedAt: string;
}

export function useTeams() {
  return useQuery({
    queryKey: ['workspace', 'teams'],
    queryFn: async (): Promise<Team[]> => {
      const res = await apiFetch<{ teams: Team[] }>('/v1/workspace/teams');
      return res.teams;
    },
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { name: string; description?: string }) =>
      apiFetch<Team>('/v1/workspace/teams', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams'] });
    },
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) =>
      apiFetch<{ success: boolean }>(`/v1/workspace/teams/${teamId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams'] });
    },
  });
}

// Composite-keyed endpoints use flat `/v1/workspace/team-members` etc.
// (the codegen's CRUD shape for composite-PK tables) — not the legacy
// nested `/v1/workspace/teams/:id/members`. Filtering by teamId happens
// client-side because the LIST endpoint is flat.

export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: ['workspace', 'teams', teamId, 'members'],
    queryFn: async (): Promise<TeamMember[]> => {
      const res = await apiFetch<{ team_members: TeamMember[] }>(`/v1/workspace/team-members`);
      return res.team_members.filter((m: TeamMember & { team_id?: string }) => m.team_id === teamId);
    },
    enabled: !!teamId,
  });
}

export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { teamId: string; userId: string }) =>
      apiFetch<{ team_id: string; user_id: string }>(`/v1/workspace/team-members`, {
        method: 'POST',
        body: JSON.stringify({ team_id: req.teamId, user_id: req.userId }),
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams', variables.teamId, 'members'] });
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams'] });
    },
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    // Bespoke composite-PK DELETE — declared in route-definitions.ts +
    // handled by handleRemoveTeamMember in routes/logic/workspace.ts.
    mutationFn: (req: { teamId: string; userId: string }) =>
      apiFetch<{ success: boolean }>(`/v1/workspace/team-members/${req.teamId}/${req.userId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams', variables.teamId, 'members'] });
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams'] });
    },
  });
}

export function useTeamResources(teamId: string) {
  return useQuery({
    queryKey: ['workspace', 'teams', teamId, 'resources'],
    queryFn: async (): Promise<TeamResource[]> => {
      const res = await apiFetch<{ team_resources: TeamResource[] }>(`/v1/workspace/team-resources`);
      return res.team_resources.filter((r: TeamResource & { team_id?: string }) => r.team_id === teamId);
    },
    enabled: !!teamId,
  });
}

export function useAssignTeamResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { teamId: string; resourceType: string; resourceId: string }) =>
      apiFetch<{ team_id: string }>(`/v1/workspace/team-resources`, {
        method: 'POST',
        body: JSON.stringify({
          team_id: req.teamId,
          resource_type: req.resourceType,
          resource_id: req.resourceId,
        }),
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams', variables.teamId, 'resources'] });
    },
  });
}

export function useRemoveTeamResource() {
  const qc = useQueryClient();
  return useMutation({
    // Bespoke composite-PK DELETE — declared in route-definitions.ts.
    mutationFn: (req: { teamId: string; resourceType: string; resourceId: string }) =>
      apiFetch<{ success: boolean }>(
        `/v1/workspace/team-resources/${req.teamId}/${req.resourceType}/${req.resourceId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'teams', variables.teamId, 'resources'] });
    },
  });
}

// ── Workspace environments / usage / entitlements / IP allowlist (apiFetch) ─────

export interface Environment {
  id: string;
  workspace_id: string;
  name: string;
  /**
   * Closed + ordered (task 67). This was `kind`, typed as the four SUGGESTED values —
   * which was wrong even under the open vocabulary it described: `kind` had no CHECK, so a
   * tenant's `uat` or `partner-sandbox` was a perfectly valid row this type said was impossible.
   * The column is genuinely closed now, so the union is finally true.
   */
  class: 'dev' | 'qa' | 'uat' | 'staging' | 'production';
  is_default: boolean;
  description: string | null;
  created_at: string;
}

export interface WorkspaceUsage {
  period_start: string;
  period_end: string;
  ai_operations: number;
  ai_operations_limit: number;
  records: number;
  records_limit: number;
  storage_gb: number;
  storage_limit_gb: number;
  integrations: number;
  integrations_limit: number;
  // Active relationships (DEBT-58 / task 68). The ROUTE has returned these since 2026-08-26;
  // this hand-written mirror of its responseBody did not, so the client could not read a field
  // the server was already sending — a primary commercial dimension, invisible to the UI.
  relationships: number;
  relationships_limit: number;
  integrations_pct: number | null;
  relationships_pct: number | null;
  workflows: number;
  workflows_limit: number;
  environments: number;
  environments_limit: number;
  overage_state: 'within' | 'over_soft' | 'over_hard';
  history: Array<{
    period_month: string;
    integrations_active: number;
    records_processed: number;
    storage_gb: number;
    ai_inference_units: number;
  }>;
  forecast: {
    days_elapsed: number;
    days_in_month: number;
    systems_at_month_end: number;
    relationships_at_month_end: number;
  };
  overage_status: {
    state: 'none' | 'recorded' | 'review';
    amount_cents: number;
    currency: string;
    period_start: string | null;
    dimensions: string[];
  };
  purchased_packs: Array<{
    dimension: string;
    unit_size: number;
    amount_pence: number;
    currency: string;
    purchased_at: string;
  }>;
}

export function useWorkspaceUsage(period: 'current' | 'previous' | 'trailing_30d' = 'current') {
  return useQuery({
    queryKey: ['workspace', 'usage', period],
    queryFn: () => apiFetch<WorkspaceUsage>(`/v1/workspace/usage?period=${period}`),
  });
}

/**
 * The workspaces this user belongs to, and which one the SERVER resolved (DEBT-56).
 *
 * `active` is the server's answer, not our stored id. The server refuses an X-Workspace-Id the
 * caller is not a member of (403 `workspace_not_member`), and `contextFetch` then drops the pin and
 * re-resolves — so the list always describes the workspace the server actually served.
 */
export interface MyWorkspace {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly tier: string;
  readonly status: string;
  readonly active: boolean;
  /** This tenant requires sign-in to name it; listed only because this session did. */
  readonly requiresNamedSignin?: boolean;
}

export function useMyWorkspaces() {
  const query = useQuery({
    queryKey: ['workspaces', 'mine', currentActiveWorkspaceId() ?? 'default'],
    queryFn: () => apiFetch<{ workspaces: MyWorkspace[] }>('/v1/workspaces'),
    select: (res) => res.workspaces,
  });

  // Belt and braces for a stale pin: a refused pin is dropped by `contextFetch` on the 403 itself;
  // this also clears one the server's answer does not mark active.
  const data = query.data;
  useEffect(() => {
    const pinned = currentActiveWorkspaceId();
    if (!pinned || !data) return;
    if (!data.some((w) => w.id === pinned && w.active)) setActiveWorkspaceId(null);
  }, [data]);

  return query;
}

/** What naming a tenant returns: the entry grant's token, stored for this browser session. */
export interface TenantEntry {
  readonly entryToken: string;
  readonly tenantId: string;
  readonly expiresAt: string;
}

/** An entry attempt the server refused — always the same answer, whatever the reason. */
export class TenantEntryRefusedError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'TenantEntryRefusedError';
  }
}

/**
 * Name the tenant this browser session is for (its link /t/<id>, or the workspace-ID box) —
 * POST /v1/session/tenant-entry. On success the entry token is stored for this session, the
 * workspace is pinned (and the environment pin cleared, as on any switch) and every query refetches.
 * On failure throws TenantEntryRefusedError carrying the server's single generic message (403), or
 * its rate-limit message (429). It never reveals whether the tenant exists.
 */
export async function enterTenant(tenantId: string): Promise<TenantEntry> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const token = (await resolveApiToken()) ?? currentApiToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Deliberately NOT the ambient context headers: naming a tenant must not depend on — or be
  // refused because of — whichever workspace this tab was pinned to before.
  const res = await fetch(`${currentApiBaseUrl()}/v1/session/tenant-entry`, {
    method: 'POST', headers, body: JSON.stringify({ tenantId: tenantId.trim() }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string };
    throw new TenantEntryRefusedError(body.message ?? "We can't sign you in to that workspace.", res.status);
  }
  const entry = await res.json() as TenantEntry;
  setTenantEntryToken(entry.entryToken);
  setActiveWorkspaceId(entry.tenantId);
  setActiveEnvironmentId(null);
  void queryClient.invalidateQueries();
  return entry;
}

/**
 * Switch workspace, clear the environment pin, then refetch everything.
 *
 * ⚠️ **Clearing the environment is not tidiness — it is correctness.** Environments belong to a
 * workspace, so an environment id from the workspace you just left is meaningless in the new
 * one. The server would ignore it and fall back to the new workspace's default environment (that is
 * the documented behaviour for X-Environment-Id, and it is why this cannot corrupt anything), but the client would go on
 * sending a header that is silently discarded, and the header bar would flicker through a name
 * that belongs to somewhere else. Drop it and let the new workspace's default resolve.
 */
export function useSwitchWorkspace() {
  return useCallback((id: string | null) => {
    setActiveWorkspaceId(id);
    setActiveEnvironmentId(null);
    void queryClient.invalidateQueries();
  }, []);
}

/**
 * The environment this session is IN (task 72) — the server's answer, never the client's guess.
 *
 * Read this rather than deriving it from the environments list plus the stored id: the server
 * resolves the same value the CRUD executor filters by, so what the header shows and what the
 * pipelines list contains are the same fact. Deriving it client-side is how those two drift.
 */
export interface ActiveEnvironment {
  readonly id: string | null;
  readonly name: string | null;
  /** Closed + ordered: dev < qa < uat < staging < production (task 67). */
  readonly class: string | null;
  readonly isDefault: boolean;
  /** False when a stored choice was ignored — the environment was deleted, or is not ours. */
  readonly pinned: boolean;
}

export function useActiveEnvironment() {
  const query = useQuery({
    queryKey: ['workspace', 'environment', currentActiveEnvironmentId() ?? 'default'],
    queryFn: () => apiFetch<ActiveEnvironment>('/v1/workspace/environment'),
  });

  // Self-heal a stale pin. If a choice is stored but the server did not honour it, the
  // environment is gone (or was never ours) and every subsequent request would carry a header
  // that is silently ignored — the user would see the default environment's data under a name
  // that no longer exists. Clear it and let the default stand.
  const data = query.data;
  useEffect(() => {
    if (data && !data.pinned && currentActiveEnvironmentId()) setActiveEnvironmentId(null);
  }, [data]);

  return query;
}

/**
 * Switch environment, then refetch EVERYTHING.
 *
 * A blanket invalidation looks blunt, and is the honest option: the environment travels as a
 * request header, so the client cannot tell which cached queries were environment-scoped and
 * which were workspace-wide — only the server knows, from the bindings. Invalidating selectively
 * would mean maintaining a second, hand-kept list of scoped queries here that drifts from the
 * manifest the moment anything is added. Switching is a deliberate, occasional action; a refetch
 * is the right price for never showing one environment's rows under another's name.
 */
export function useSwitchEnvironment() {
  return useCallback((id: string | null) => {
    setActiveEnvironmentId(id);
    void queryClient.invalidateQueries();
  }, []);
}

/**
 * The customer's active entitlement codes (e.g. `module_data_protection`). Used
 * to gate the platform sidebar nav — `AppNavEntry.showWhen({ entitlements })`,
 * emitted by the app-pages generator for module-gated entities.
 */
export function useEntitlements() {
  return useQuery({
    queryKey: ['workspace', 'entitlements'],
    queryFn: () => apiFetch<{ entitlements: string[] }>('/v1/workspace/entitlements'),
    staleTime: 5 * 60_000,
  });
}

/** The workspace's IP/CIDR allowlist (VPN/office restriction; enforced server-side). */
export function useIpAllowlist() {
  return useQuery({
    queryKey: ['workspace', 'ip-allowlist'],
    queryFn: () => apiFetch<{ ipAllowlist: string[] }>('/v1/workspace/ip-allowlist'),
  });
}

export function useUpdateIpAllowlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ipAllowlist: string[]) =>
      apiFetch<{ ipAllowlist: string[] }>('/v1/workspace/ip-allowlist', { method: 'PUT', body: JSON.stringify({ ipAllowlist }) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'ip-allowlist'] }); },
  });
}

export function useCreateEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; class: Environment['class']; description?: string }) =>
      apiFetch<Environment>('/v1/workspace/environments', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'environments'] });
      void qc.invalidateQueries({ queryKey: ['workspace', 'usage'] });
    },
  });
}

export function useDeleteEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ success: boolean }>(`/v1/workspace/environments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', 'environments'] });
      void qc.invalidateQueries({ queryKey: ['workspace', 'usage'] });
    },
  });
}

// ── SSO (workspace) — owner-managed SAML/OIDC providers + role mapping (E17) (apiFetch) ──

export type SsoRole = 'viewer' | 'operator' | 'editor' | 'admin';

export interface SsoProvider {
  id: string;
  providerId: string;
  type: 'saml' | 'oidc';
  displayName: string;
  enabled: boolean;
  createdAt: string;
}

export interface SsoRoleMappingEntry {
  group: string;
  role: SsoRole;
}

export interface CreateSsoProviderInput {
  providerId: string;
  displayName: string;
  type: 'saml' | 'oidc';
  // SAML
  idpEntityId?: string | undefined;
  ssoUrl?: string | undefined;
  certificate?: string | undefined;
  // OIDC
  clientId?: string | undefined;
  issuer?: string | undefined;
  clientSecret?: string | undefined;
}

export function useSsoProviders() {
  return useQuery({
    queryKey: ['workspace', 'sso', 'providers'],
    queryFn: () => apiFetch<{ providers: SsoProvider[]; maxProviders: number; deploymentMode: string; sovereignMode: boolean }>(
      '/v1/workspace/sso/providers',
    ),
  });
}

export function useCreateSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSsoProviderInput) =>
      apiFetch<{ provider: SsoProvider; sovereignMode: boolean }>('/v1/workspace/sso/providers', {
        method: 'POST', body: JSON.stringify(input),
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'sso', 'providers'] }); },
  });
}

export function useDeleteSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (providerId: string) =>
      apiFetch<{ deleted: boolean; providerId: string }>(`/v1/workspace/sso/providers/${encodeURIComponent(providerId)}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'sso', 'providers'] }); },
  });
}

export function useSsoRoleMapping() {
  return useQuery({
    queryKey: ['workspace', 'sso', 'role-mapping'],
    queryFn: () => apiFetch<{ mappings: SsoRoleMappingEntry[]; maxEntries: number }>('/v1/workspace/sso/role-mapping'),
  });
}

export function useUpdateSsoRoleMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappings: SsoRoleMappingEntry[]) =>
      apiFetch<{ mappings: SsoRoleMappingEntry[]; updatedAt: string }>('/v1/workspace/sso/role-mapping', {
        method: 'PUT', body: JSON.stringify({ mappings }),
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workspace', 'sso', 'role-mapping'] }); },
  });
}

// Pair-scoped guidance retrieval (C5 pilot) — enabled:false until the tenant opts in via
// the guidance.pairScopedEnabled tunable; retrieval only, never mutates the mapping.
export interface MappingGuidanceResult {
  enabled: boolean;
  providers?: string[];
  results: Array<{ snippet: string; source: string; score: number | null }>;
  error?: string;
}

export function fetchMappingGuidance(mappingId: string, q?: string): Promise<MappingGuidanceResult> {
  const params = q && q.trim() !== '' ? `?${new URLSearchParams({ q }).toString()}` : '';
  return apiFetch<MappingGuidanceResult>(`/v1/mappings/${mappingId}/guidance${params}`);
}
